import { useStyles } from "./styles";
import { useTheme } from "@emotion/react";
import { useLocation, useNavigate } from "react-router-dom";
import BasicTable from "components/table";
import React, { useCallback, useEffect, useRef, useState } from "react";
import constants from "../../constants/constants.json";
import { Avatar, Box, Button, Grid2, Link, Typography } from "@mui/material";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import { useParams } from "react-router";
import {
  addTemplateRow,
  deleteTemplateRow,
  getOverviewLevel3,
  getTemplateList,
  refreshTemplateList,
  updateTemplateRow,
} from "./parametricAnalysis.services";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import { useAuth0 } from "@auth0/auth0-react";
import PropTypes from "prop-types";
import { find } from "lodash";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { getToken } from "utils/loadAuth";
import FormModal from "components/formDialog";
import { getFormFields } from "./utils";
import ModalPopup from "components/modalPopup";
import Notification from "components/snackbar";
import { getAnalysisPath, openSpotfireAnalysis } from "./spotfireMashup";

const MASHUP_ELEMENT_ID = "spotfire-mashup";

const ParametricAnalysis = ({
  productId,
  isNavbarOpen,
  disableVirtualization = false,
  product,
}) => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [openPlayer, setOpenPlayer] = useState(false);
  const [analysisPath, setAnalysisPath] = useState("");
  const [playerError, setPlayerError] = useState("");
  const appRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshLoading, setIsRefreshLoading] = useState(false);
  const [notificationData, setNotificationData] = useState({
    message: "",
    serverity: "",
  });
  const [openAlert, setOpenAlert] = useState(false);
  const [templateForm, setTemplateForm] = useState();

  const { message, serverity } = notificationData;

  const [openDialog, setOpenDialog] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [rowData, setRowData] = useState();
  const [openDelete, setOpenDelete] = useState(false);
  const [isFromShip, setIsFromShip] = useState(false);

  const [data, setData] = useState([]);
  const theme = useTheme();
  const navigate = useNavigate();
  const { pid, fid, tid } = useParams();
  const location = useLocation();

  const defaultSort = { field: "Name", sort: "asc" };
  const [sort, setSort] = useState([defaultSort]);
  const [canAddSst, setCanAddSst] = useState(false);
  const [parametricPaginationModel, setParametricPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const onPaginationModelChange = ({ page, pageSize }) => {
    setParametricPaginationModel({
      page,
      pageSize,
    });
  };

  const mapResponse = useCallback((data) => {
    return data?.map(({ path: Name, guid: id, ...rest }) => ({
      Name,
      id,
      ...rest,
    }));
  }, []);

  const onRefresh = async () => {
    try {
      const token = await getToken({
        getAccessTokenSilently,
        loginWithRedirect,
      });

      if (token) {
        setIsRefreshLoading(true);
        let result = await refreshTemplateList({
          token,
        });
        if (result.templates) {
          setIsRefreshLoading(false);
          const data = mapResponse(result?.templates);
          const formFields = getFormFields(data, result?.updatedAt);
          setTemplateForm(formFields);
        }
      }
    } catch (error) {
      console.log(error);
      setIsRefreshLoading(false);
    }
  };

  const submitHandler = async (formData) => {
    formData.product_id = productId;
    const token = await getToken({
      getAccessTokenSilently,
      loginWithRedirect,
    });
    if (!token) return;
    if (isEdit) {
      let res = await updateTemplateRow({
        token,
        formData,
      });
      if (res?.length) {
        setOpenAlert(true);
        setNotificationData({
          ...notificationData,
          serverity: "success",
          message: constants.editSuccess,
        });
        setData(res);
      } else {
        setOpenAlert(true);
        setNotificationData({
          ...notificationData,
          serverity: "error",
          message: res?.message ? res?.message : constants.errorMessage,
        });
      }
    } else {
      formData.is_ship_template = true;
      let res = await addTemplateRow({
        token,
        formData,
      });
      if (res.length) {
        setOpenAlert(true);
        setNotificationData({
          ...notificationData,
          serverity: "success",
          message: constants.addSuccess,
        });
        setData(res);
      } else {
        setOpenAlert(true);
        setNotificationData({
          ...notificationData,
          serverity: "error",
          message: res?.message ? res?.message : constants.errorMessage,
        });
      }
    }
    setRowData({});
    setOpenDialog(false);
  };

  const renderWebplayerButton = (value) => {
    setIsFromShip(value?.row?.is_ship_template);
    const shipLogo = constants.assets.shipLogo;
    const isShipTemplate =
      constants.shipProductMap.includes(product?.product_name) ||
      constants.shipProductTemplateMap.includes(value?.row.detail_link) ||
      value?.row?.is_ship_template;
    return (
      <Button
        data-testid={"webplayer-btn"}
        onClick={() => handleViewWebPlayerClick(value)}
        className={classes.webplayerBtn}
        variant="contained"
        {...{
          endIcon: isShipTemplate ? (
            <Avatar sx={{ width: 15, height: 15 }} src={shipLogo} />
          ) : null,
        }}
      >
        {constants.webplayer}
      </Button>
    );
  };

  const onSortModelChange = (event) => {
    if (JSON.stringify(event) !== JSON.stringify(sort)) {
      setSort(event);
    }
  };

  const queryParams = useCallback(() => {
    //  new URLSearchParams automatically decodes the URL-encoded string
    return new URLSearchParams(location.search);
  }, [location.search]);

  const isShipTemplate =
    constants.shipProductMap.includes(product?.product_name) ||
    constants.shipProductTemplateMap.includes(queryParams().get("id")) ||
    isFromShip;

  const getTemplateName = () => {
    try {
      const templateValue = queryParams().get("id");
      if (templateValue) {
        return queryParams().get("id").split("/").pop();
      }
      return null;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const tidName = decodeURIComponent(getTemplateName());

  const handle = useFullScreenHandle();

  const classes = useStyles({
    theme,
    isNavbarOpen,
    openPlayer,
    isWebPlayerExpanded: handle.active,
  });
  const onBackClick = useCallback(() => {
    navigate(`/overview/entry/${pid}/${Number(fid)}`);
    setOpenPlayer(false);
    setAnalysisPath("");
    setPlayerError("");
  }, [pid, fid, navigate]);
  const handleViewWebPlayerClick = (value) => {
    navigate(
      `/overview/entry/${pid}/${Number(fid)}?id=${encodeURIComponent(
        value.row.detail_link,
      )}`,
    );

    setPlayerError("");
    setAnalysisPath(getAnalysisPath(value.row.detail_link));
    setOpenPlayer(true);
    setIsFromShip(value?.row?.is_ship_template);
  };

  const columns = [
    { field: "name", headerName: "Name", flex: 0.07 },
    {
      field: "description",
      headerName: "Description",
      flex: 0.1,
    },
    {
      field: "documentation_label",
      headerName: "Documentation",
      flex: 0.05,
      renderCell: (value) => {
        if (value?.row?.documentation_link && value?.row?.documentation_label) {
          return (
            <Link
              href={value.row.documentation_link}
              target="_blank"
              sx={{ color: theme.palette.text.primary }}
            >
              {value.row.documentation_label}
            </Link>
          );
        }
      },
    },
    {
      field: "version",
      headerName: "Version",
      flex: 0.03,
    },
    {
      field: "detail_link",
      headerName: "Details",
      flex: canAddSst ? 0.032 : 0.04,
      sortable: false,
      headerClassName: classes.detailsHeader,
      renderCell: (value) => {
        return <>{renderWebplayerButton(value)}</>;
      },
    },
  ];

  if (canAddSst) {
    columns.push({
      field: "Actions",
      headerName: "Actions",
      flex: 0.04,
      sortable: false,
      renderCell: (value) => {
        return (
          <Box
            data-testid={"action-container"}
            className={classes.actionsContainer}
          >
            <Button
              data-testid={"edit-btn"}
              className={classes.actionBtn}
              onClick={() => editHandler(value.row)}
              title={constants.edit}
            >
              <EditIcon />
            </Button>

            <Button
              data-testid={"delete-btn"}
              className={classes.actionBtn}
              onClick={() => deleteHandler(value.row)}
              title={"Delete"}
            >
              <DeleteIcon />
            </Button>
          </Box>
        );
      },
    });
  }

  const editHandler = (data) => {
    setIsEdit(true);
    setRowData(data);
    setOpenDialog(true);
  };

  const deleteHandler = (data) => {
    setRowData(data);
    setOpenDelete(true);
  };

  const deleteTemplate = async () => {
    const token = await getToken({
      getAccessTokenSilently,
      loginWithRedirect,
    });
    if (token) {
      let res = await deleteTemplateRow({
        token,
        id: rowData ? rowData?.id : 0,
        productId: productId,
      });
      if (res) {
        setOpenAlert(true);
        setNotificationData({
          ...notificationData,
          serverity: "success",
          message: constants.deleteSuccess,
        });
        setData(res);
      } else {
        setOpenAlert(true);
        setNotificationData({
          ...notificationData,
          serverity: "error",
          message: constants.errorMessage,
        });
      }

      setOpenDelete(false);
    }
  };

  const getTableData = useCallback(
    async (token) => {
      try {
        const templateValue = queryParams().get("id");
        //base64 encode template value to handle special characters
        const encodedTemplateValue = templateValue ? btoa(templateValue) : null;

        let res = await getOverviewLevel3({
          productId,
          template: encodedTemplateValue,
          token,
        });
        if (res) {
          setData(res.items);
        }
      } catch (error) {
        if (error.status === 403) {
          navigate("/notfound");
        }
        console.log(error);
      }
    },
    [queryParams, productId, navigate],
  );

  const getParametricList = useCallback(
    async (token) => {
      try {
        let result = await getTemplateList({
          token,
          product_id: productId,
        });

        if (result?.message === "SST not availabe for this user") {
          setCanAddSst(false);
        }

        if (result?.templates) {
          const data = mapResponse(result?.templates);
          const formFields = getFormFields(data, result?.updatedAt);
          setTemplateForm(formFields);
          setCanAddSst(true);
        }

        setIsLoading(false);
      } catch (error) {
        console.log(error);
        setIsLoading(false);
      }
    },
    [productId, mapResponse],
  );

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = await getToken({
        getAccessTokenSilently,
        loginWithRedirect,
      });

      if (token) {
        await getParametricList(token);
        await getTableData(token);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }, [
    getAccessTokenSilently,
    loginWithRedirect,
    getTableData,
    getParametricList,
  ]);

  const getMashup = useCallback(
    (margin) => {
      return (
        <div className={classes.iframeWrapper} data-testid={"webplayer-mashup"}>
          {playerError && (
            <Typography color="error" sx={{ p: 2 }}>
              {playerError}
            </Typography>
          )}
          <div
            id={MASHUP_ELEMENT_ID}
            style={{ marginTop: margin }}
            className={classes.iframe}
          />
        </div>
      );
    },
    [playerError, classes.iframeWrapper, classes.iframe],
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!openPlayer || !analysisPath) {
      return undefined;
    }

    let cancelled = false;
    setPlayerError("");

    openSpotfireAnalysis({
      server: constants.spotfireBaseUrl,
      path: analysisPath,
      elementId: MASHUP_ELEMENT_ID,
    })
      .then(({ app }) => {
        if (cancelled) {
          app.close();
          return;
        }
        appRef.current = app;
      })
      .catch((error) => {
        console.error("Spotfire mashup error", error);
        if (!cancelled) {
          setPlayerError(constants.errorMessage);
        }
      });

    return () => {
      cancelled = true;
      if (appRef.current) {
        try {
          appRef.current.close();
        } catch (error) {
          console.error(error);
        }
        appRef.current = null;
      }
    };
  }, [openPlayer, analysisPath]);

  useEffect(() => {
    if (data.length === 0) {
      return;
    }

    const templateValue = queryParams().get("id");
    const template = find(data, ["detail_link", templateValue]);
    if (templateValue && template?.detail_link) {
      handleViewWebPlayerClick({ row: template });
    } else {
      onBackClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onBackClick, tid, data.length, location.search]);

  return (
    <>
      {!openPlayer ? (
        <Box className={classes.container} key={location?.key}>
          {canAddSst && (
            <Box className={classes.actionContainer}>
              <Box sx={{ display: "flex" }}>
                <Button
                  data-testid={"add-btn"}
                  variant="contained"
                  className={classes.createBtn}
                  onClick={() => {
                    setIsEdit(false);
                    setOpenDialog(true);
                  }}
                >
                  {constants.addTemplate}
                </Button>
              </Box>
            </Box>
          )}
          <Box
            className={classes.parametricContainer}
            data-testid={"parametric-analysis"}
          >
            <Box className={classes.tableWrapper}>
              <BasicTable
                data-testid={"data-grid"}
                columns={columns}
                onSortModelChange={onSortModelChange}
                sortModel={sort}
                paginationModel={parametricPaginationModel}
                onPaginationModelChange={onPaginationModelChange}
                rows={data}
                disableColumnMenu={true}
                disableVirtualization={disableVirtualization}
                isLoading={isLoading}
              />
            </Box>
          </Box>
        </Box>
      ) : (
        <Box className={classes.webplayerWrapper}>
          <Grid2
            display={"flex"}
            justifyContent={"space-around"}
            className={classes.webplayerHeaderGrid}
          >
            <Box className={classes.tidNameWrapper}>
              {isShipTemplate && (
                <img
                  data-testid={"ship-logo"}
                  src={constants.assets.shipLogo}
                  alt="Ship logo"
                  className={classes.shipLogo}
                />
              )}
              <Typography variant="h5" className={classes.tidName}>
                {tidName}
              </Typography>
            </Box>
            <Box className={classes.btnContainer}>
              <Button
                data-testid={"back-btn"}
                className={classes.backBtn}
                variant="contained"
                onClick={onBackClick}
              >
                <ArrowLeftIcon fontSize="small" className={classes.backIcon} />
                Back
              </Button>
              <Button
                data-testid={"expand-btn"}
                sx={{ m: 0, p: 0, minWidth: 40 }}
                size="small"
                variant="contained"
                className={classes.expandIcon}
                onClick={() => {
                  handle.enter();
                }}
              >
                <AspectRatioIcon fontSize="small" />
              </Button>
            </Box>
          </Grid2>
          <FullScreen handle={handle}>{getMashup("0")}</FullScreen>
        </Box>
      )}
      <FormModal
        open={openDialog}
        onClose={() => {
          setRowData({});
          setOpenDialog(false);
        }}
        title={isEdit ? constants.editTemplate : constants.addTemplate}
        formFields={templateForm || []}
        primaryBtnText={constants.close}
        secondaryBtnAction={submitHandler}
        secondaryBtnText={constants.submit}
        rowData={isEdit ? rowData : {}}
        setRefreshClicked={() => onRefresh()}
        refreshLoading={isRefreshLoading}
      />
      <ModalPopup
        open={openDelete}
        title={`Delete ${rowData?.name}?`}
        message={
          <>
            <b>{rowData?.name}</b> {constants.deleteTemplateMsg} <br />
            <b>Template Path:</b> {rowData?.detail_link}
          </>
        }
        primaryBtnText={constants.cancel}
        secondaryButtonText={constants.confirm}
        handleClose={() => setOpenDelete(false)}
        secondarybtnAction={deleteTemplate}
      />
      <Notification
        message={message}
        open={openAlert}
        setOpen={setOpenAlert}
        severity={serverity}
      />
    </>
  );
};

// props validation

ParametricAnalysis.propTypes = {
  productId: PropTypes.any,
  isNavbarOpen: PropTypes.bool,
  disableVirtualization: PropTypes.bool,
  product: PropTypes.object,
};

export default React.memo(ParametricAnalysis);
